<!-- eslint-disable vue/prop-name-casing -->
<script setup>
import { computed, ref } from 'vue';
import { useModal } from 'vue-final-modal';
import { useTerraStore } from '~/terra/store/terra.store.js';
import PatternGeneratePopup from './pattern-generate-popup.vue';

const props = defineProps({
  feature: {
    type: Object,
    default: null,
  },
  ftg: {
    type: Object,
    default: null,
  },
  is_symbology: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['close', 'addFeature', 'delete']);
const $t = inject('$t');
const terra_store = useTerraStore();
const feature_form = ref(null);
const is_saving = ref(false);
const fill_options = computed(() => [
  { value: 'fill_color', label: $t('Fill color') },
  { value: 'fill_pattern', label: $t('Fill pattern') },
]);
const columns = ref({
  lg: {
    container: 12,
    label: 4,
    wrapper: 12,
  },
});
const color_list = ref([
  { label: 'Red', value: '#E11845' },
  { label: 'Yellow', value: '#F2CA19' },
  { label: 'Green', value: '#13D10B' },
  { label: 'Blue', value: '#1568E9' },
  { label: 'Orange', value: '#FF9640' },
  { label: 'Purple', value: '#9A42FF' },
  { label: 'Pink', value: '#FF00BD' },
  { label: 'Maroon', value: '#B40000' },
  { label: 'Grey', value: '#B3B3B3' },
  { label: 'Black', value: '#000000' },
]);

const title = computed(() => {
  if (props.feature) {
    if (props.is_symbology)
      return $t('Class Symbology');
    else
      return $t('Edit Class');
  }
  else {
    return $t('Add Class');
  }
});

const is_name_disabled = computed(() => props.is_symbology && !terra_store.check_permission('modify_featuretypes'));

const fill_colors = computed(() => {
  return [
    { label: 'None', value: '' },
    ...color_list.value,
    { label: 'Custom color', value: color_list.value.findIndex(item => item.value === feature_form.value?.fill_color) === -1 ? feature_form.value?.fill_color : '', visible: false },
  ];
});

const outline_colors = computed(() => {
  return [
    ...color_list.value,
    { label: 'Custom color', value: color_list.value.findIndex(item => item.value === feature_form.value?.color) === -1 ? feature_form.value?.color : '', visible: false },
  ];
});

const patterns = ref([
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAG4AAABuCAYAAADGWyb7AAAAAXNSR0IArs4c6QAAApZJREFUeF7t3FGOwjAMhOFwPjhuOV9XhYUttJXgod6M+vGMRIjrifN73NMwDOP5fG5bn+v12i6XSxuGofne3y79976cxnEcBe2+A/8djG+SYzNwSX/iiEqwGjhBW9egnvZlEbieFre2fdZ335WXwNmU/jPtscJn4AQtJ2jPjBO0rKDdAjfd49zTloHr/WE+tdbGb+4PRyy9e7zn3jJOMPohIp8mEXIyS6fe5XG+PuTkN3BJQZsUEjkJY5SPYw05Ce1+ICehLSvkJLTPeAtc2sHc472qGmIgJxtPQe8PM3KyErjegzatDzl5C1xC0CZZRk6QEy6wefLunbnICXKS14xMs+TNuzjICXLymnF7a/zRL+HISaj9HjlBTrK89+l2DeQEOTGK9a66exZoPCc8J4YjHxm3Z6bxnIRm2mLoIxn/VJ4tvVSjPCc8J3k27k/t3j1+DzlBTpCTSvCNnCAnyElldYuchN7neE54TnhOKiEGzwnPCc9J5eAHzwnPyT3jKloaa6fp0X7XtI5pHW+SrVQC0zqmdUzrmNYJbW7ynISW6JVVNXKCnCAnyEloc7PSm8JzwnMCd/GcYJ5bz8CTBSMnyAlygpwgJy9Suday8oZYb4hdnqdHa25W/l/TOqGyzHMSCrSRE+QEOUFOkBPkpLK5aVontLmJnISW6DwnoSV6pSzznITKMnISKsvISagsIyfICXKCnCAnyElliY6chJboyEloiY6chJbolbKMnITKMnISKsvISagsIyfICXKCnCAnyElliY6chJboyEloiY6chJbolbKMnITKMnISKsvISagsIyfICXKCnCAnyElliY6chJboyEloiY6chJbolbKMnITKMnISKsvISagsIyfICXJSSU5+AOr4njXlhVGJAAAAAElFTkSuQmCC',
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAG4AAABuCAYAAADGWyb7AAAAAXNSR0IArs4c6QAAAkxJREFUeF7t3EGOwkAMBVG4VO6/zqUymiwBISFso995sx7ZTlXsVqwW9+M4jpu/OAJ34uKcnQUTl+mNuFBvz+K2bTufZd/3t8/k/17jmeLyNCqnEj8+tryfvQjOuNBZSRxxoQRCy9ZxxIUSCC1bxxEXSiC0bB23ijgfwp99CP9qkWBzErriMypXGZWhz3G5snVcqHLiiAslEFq2jiMulEBo2TpuFXE2JzYnJwEvQs+LYFSuMipDn+NyZeu4UOXEERdKILRsHUdcKIHQsnXcKuJ8MPd8MFffTXHnxJ2T0JkTWrYzjrhQAqFl6zjiQgmElq3jiAslEFq2jltFnM2JzYk7J2+6+dsGMSpXGZWhz3G5snVcqHLiiAslEFq2jiMulEBo2TpuFXHffhhW360Q7/Uv9rpz4s5J6MwJLdsZR1wogdCydRxxoQRCy9ZxxIUSCC1bx60izubEnRN3Ttw5CZ1njWU74xrhdoYmrpNuY2ziGuF2hiauk25jbOIa4XaGJq6TbmNs4hrhdoZ2dWGVqwtWXqErr872FruOgDOujuVoJOJGcdclI66O5Wgk4kZx1yUjro7laCTiRnHXJSOujuVoJJsTm5OMjcNjlambIqNydMDVJSOujuVoJOJGcdclI66O5Wgk4kZx1yUjro7laCTiRnHXJSOujuVoJJsTmxObk38CU5sYo3J0wNUlI66O5Wgk4kZx1yUjro7laCTiRnHXJSOujuVoJOJGcdclI66O5WgkmxObE5sTm5PRoZOZzBmX6e1GHHGhBELL/gOuI4zzfU518wAAAABJRU5ErkJggg==',
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAG4AAABuCAYAAADGWyb7AAAAAXNSR0IArs4c6QAAAtZJREFUeF7tnMFtwkAQRccHqvGBE/KJHijAXUAZdAEF0AMnxNnV+OAIJ0GE2AoSZO2HH2dL/sxnd2ceM5s1TdOEH1wEMo3DedYKbo07nU5RVVXkeR5FUfR+E5/rDs0QccnW63VzOBxiuVzG8XiM1WoV2+32l8LNZhM+N564ZPP5vDmfzzGbzaKu61gsFnExqSzLq3n7/b410+fGE5dst9s1msT7UXYmJ26L4z8uerPKIQ7cy97sex9LgCwHmNXAZzkA1T5p2RoHtV/jyMaZEDyWEPR5PET8JCdQUiQ5gZIiyQkU50lOIlo2SwPokpOvjGOIBOMZUmQ5QC4HoNonLdsVB7Vf48jG0Q7mMRGMZxKMZ76H5ERyEmFvSrqeHcmJ5MTWvu8zKwWJkZxITqB5NVS2dZzGQSMAle2KIxsnObHnBPmnJHE6yZ4Te07S4R5HvhbdLegpKn/i9jSmuEhOJCfQvBoq2zpO46ARgMp2xZGNk5xITiQniXpYJCeSE8nJZcNN1TDltI7TOl7gdpvi/HfCZzlALgeg2ict2xUHtV/jyMb990H6zFTKUNMwY3+v0zqJSMer/4SVnEhOJCeSEy/2/pEWdG2z9pzYcwLNq6GyreM0DhoBqGxXHNk4yYk9J/acJCIxkhPJieREciI5kZy8a+JlOUAuB6DaJy3bFQe1X+PIxr3rAf7OvS72nCQiHfacQEnHq6/48IZYb4j1htjvM/XV26I9J0XRm0PSEjTLAXI5ANU+admuOKj9Gkc2jnYwvzMReXRKSHIiOUl3VcSlrinL8rrwUl1RMab3Sk4kJ5ITyUlVRZ57b8ptEnafQFoOkMsBqPZJy3bFQe3XOLJxkhOndZzWSURinNaB9rBITiQnkhPJieTkV7YkOYGm//eyreOgRmqcxkEjAJXdrjjJieREciI5sYelrvuvIJGcSE4kJ5ITyYnkBJrt/ynbAvzPEI3zgQ8ZZsOF2MoxCwAAAABJRU5ErkJggg==',
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAG4AAABuCAYAAADGWyb7AAAAAXNSR0IArs4c6QAAAr1JREFUeF7tnFGSgjAQBfFO3gLuZHknuAV3Yitxgxt0Sz9IoCvtl9ZSOjsvCfM6Ey7LsiydL1wGLgqH0ywGvAp3v9+76/Xa9X0f/zBNUzfPc3e73bL/zOvOkZdVuCDUMAzdOI5RqPQ+CZnU87pHjo7OS7ZUJlGCSO+C24rndfkyWzN/CjcMMfu0gepSCb01WJxAizHtANMNPO0ANP5mw3bGQaVXOLpwEpFzEJE0jj7poR2g24HEJwPqIhrSvyteTYJx1O9KTqAD1aWSvlR+uhl+e9P0ujrbYtoBuh2Axt9s2M44qPQKRxfO4kRysjYb2cNSrjdFA0434CKv19lx5uYoyYnkxMbZmg3F2gG6HYDG32zYzjio9ApHF05yIjmRnFQ47SQ5kZy83ixaaNo5vFnIA4vnOLC4xWz/HTT1tI6ndaB1NTRsfZzCQTMADdsZRxdOciI5kZxITvJ1TEP/aG8PL5EXHXlJTiQncSpb7JQtdrQDdDsAjb/ZsJ1xUOkVji6cxUTZYiKNj73ybAs6vQU9jAjJxJNMHNWS8O3vSk4kJ/lolcSUJTH2nNhzAq2roWHr4xQOmgFo2M44unB7Ofq9CYHf9/6hbpITycnrmiOJKUdiJCeSE8lJzUdgSU4kJ9C6Ghq2Pk7hoBmAhu2MowsnObHnJI5hN1IrbaTacwJ90KjCQYVzaSu7tKUaaK88S04kJ9C6Ghq2Pk7hoBmAhu2MowsnOZGcSE58zkm+jtnD4nNOslUhfAgP9Ox/Pd32tnfGAWN7Hr09z+IEWpxAq+Jmw9bHQaVXOIWDZgAatts69G2dvTb49t4w9PvGmILU3p68poc+6Ic+7DmRnAiZa0JmyYnkBFpgs8LWgLP0WqNVOIWDZgAatuREcuLh/UMO72vAoQZc4aDCCZk9rRPrNElMWRKjj6PbAWj8zYb9A7OUaB8GKLGWAAAAAElFTkSuQmCC',
  'data:image/png;base64,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',
]);

function progressFormat(value) {
  return `${value * 100}%`;
}

async function saveFeatureType() {
  is_saving.value = true;
  try {
    let order_index = props.ftg?.featureTypes?.length;
    if (!Number.isNaN(Number(props.feature?.order_index)))
      order_index = props.feature.order_index;
    const data = {
      name: feature_form.value.name,
      featureTypeGroup: props.ftg?.uid,
      order_index,
      properties: {
        color: feature_form.value.color || color_list.value[0].value,
        fill_type: feature_form.value.fill_type,
        ...(feature_form.value.fill_type === 'fill_color' && {
          fill_color:
                    feature_form.value.fill_color,
          opacity: feature_form.value.opacity,
        }),
        ...(feature_form.value.fill_type === 'fill_pattern' && {
          fill_pattern: feature_form.value.fill_pattern,
        }),
      },
    };
    if (props.ftg?.uid) {
      await terra_store.update_feature_type({
        feature_type_uid: (props.feature ? props.feature.uid : null),
        group_uid: props.ftg.uid,
        data,
        action: props.feature ? 'update' : 'add',
      });

      terra_store.update_patterns_on_map(
        feature_form.value.fill_type === 'fill_pattern'
          ? {
              data: feature_form.value.fill_pattern,
              feature_id: props.feature?.uid,
            }
          : {},
      );

      terra_store.update_features_styles();
      setTimeout(() => {
        (async () => {
          await terra_store.update_map_features_and_polygon();
        })();
      }, 500);
    }
    else {
      emit('addFeature', { ...(props.feature || {}), ...data });
    }
    is_saving.value = false;
    emit('close');
  }
  catch (err) {
    logger.log('🚀 ~ file: edit-feature-type-modal.vue:84 ~ saveFeatureType ~ err:', err);
  }
}

function formLoadData() {
  const default_form = {
    name: '',
    fill_type: 'fill_color',
    color: '#E11845',
    fill_color: '',
    opacity: 1,
    fill_pattern: patterns.value[0],
  };
  feature_form.value = {
    ...default_form,
    ...(props.feature?.properties || {}),
    name: props.feature?.name || '',
  };
  if (!feature_form.value.color)
    feature_form.value.color = 'rgb(128, 128, 128)';
}
const pattern_generate_popup = useModal({
  component: PatternGeneratePopup,
  attrs: {
    onClose() {
      pattern_generate_popup.close();
    },
    onApply: async (pattern) => {
      feature_form.value.fill_pattern = pattern;
    },
  },
});
formLoadData();
</script>

<template>
  <hawk-modal-container :width="400" content_class="rounded-lg min-w-[600px]">
    <Vueform
      v-model="feature_form"
      :sync="true" size="sm"
      class="mb-3"
      :columns="columns"
      :display-errors="false" :endpoint="saveFeatureType"
    >
      <div class="col-span-12">
        <hawk-modal-header @close="$emit('close')">
          <template #title>
            {{ title }}
          </template>
        </hawk-modal-header>
        <div class="h-[300px] py-3 px-5">
          <TextElement
            class="my-4"
            name="name"
            :add-classes="is_name_disabled ? {
              TextElement: {
                inputContainer: ['!border-0', '!bg-white'],
                input: ['!text-gray-900', '!font-sm'],
              },
            } : {}"
            :disabled="is_name_disabled"
            :placeholder="$t('Enter name')"
            :label="$t('Name')"
            :rules="['required']"
          />
          <SelectElement
            class="my-4"
            name="color"
            :label="$t('Outline color')"
            :placeholder="$t('Outline color')"
            :items="outline_colors"
            :can-clear="false"
            :limit="10"
            :native="false"
            :can-deselect="false"
          >
            <template #before-list>
              <div class="p-3 border-b">
                <label for="outline" class="flex items-center min-w-full">
                  <div class="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
                  <span class="text-[14px] ml-2">
                    {{ $t('Custom Color') }}
                  </span>
                </label>
                <input id="outline" v-model="feature_form.color" type="color" class="opacity-0 invisible absolute">
              </div>
            </template>
            <template #single-label="{ value }">
              <div class="flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3">
                <div class="w-2 h-2 rounded-full mr-2" :style="{ backgroundColor: value.value }" />
                <div class="text-[14px]">
                  {{ value.label === "Custom color" ? value.value : value.label }}
                </div>
              </div>
            </template>
            <template #option="optionProps">
              <div v-if="optionProps.option.label !== 'Custom color'" class="flex items-center">
                <span class="w-2 h-2 rounded-full mr-2 ml-1" :style="{ backgroundColor: optionProps.option.value }" />
                <span class="text-[14px]">{{ optionProps.option.label }}</span>
              </div>
              <div v-else class="absolute bg-white -m-2 min-w-full">
                {{ optionProps.option.label }}
              </div>
            </template>
          </SelectElement>
          <RadiogroupElement
            class="my-4"
            name="fill_type"
            :items="fill_options"
            :inline="true"
            :add-classes="{
              RadiogroupRadio: {
                container: 'mr-5 flex text-[14px]',
                text: 'font-medium text-[14px]',
              },
            }"
            :remove-class="{
              wrapper: ['flex-col'],
            }"
          >
            <template #item="{ item }">
              <v-radio :value="item.value">
                {{ item.label }}
              </v-radio>
            </template>
          </RadiogroupElement>
          <div v-show="feature_form && feature_form.fill_type !== 'fill_pattern'">
            <SelectElement
              class="my-4"
              name="fill_color"
              :label="$t('Fill color')"
              :placeholder="$t('Choose Color')"
              :items="fill_colors"
              :can-clear="false"
              :limit="11"
              :native="false"
              :can-deselect="false"
            >
              <template #before-list>
                <div class="p-3 border-b">
                  <label for="fill" class="flex items-center min-w-full">
                    <div class="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
                    <span class="text-[14px] ml-2">
                      {{ $t('Custom Color') }}
                    </span>
                  </label>
                  <input id="fill" v-model="feature_form.fill_color" type="color" class="opacity-0 invisible absolute">
                </div>
              </template>
              <template #single-label="{ value }">
                <div class="flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3">
                  <div class="w-2 h-2 rounded-full mr-2" :style="{ backgroundColor: value.value }" />
                  <div class="text-[14px]">
                    {{ value.label === "Custom color" ? value.value : value.label }}
                  </div>
                </div>
              </template>
              <template #option="{ option }">
                <div v-if="option.label !== 'Custom color'" class="flex items-center">
                  <span class="w-2 h-2 rounded-full mr-2 ml-1" :style="{ backgroundColor: option.value }" />
                  <span class="text-[14px]">{{ option.label }}</span>
                </div>
                <div v-else class="absolute bg-white -m-2 min-w-full">
                  {{ option.label }}
                </div>
              </template>
            </SelectElement>
            <SliderElement
              name="opacity" :label="$t('Fill Opacity')" class="my-4" tooltip-position="bottom"
              :format="progressFormat"
              :min="0"
              :max="1"
              :step="0.01"
            />
            <TextElement
              class="my-4 invisible"
              name="fill_pattern"
              placeholder="Pattern name"
              label="Pattern name"
            />
          </div>

          <div v-show="feature_form && feature_form.fill_type === 'fill_pattern'">
            <div class="my-2">
              <label for="fill_pattern" class="text-[14px]">{{ $t('Chooose Pattern') }}</label>
              <div class="flex py-1.5">
                <img
                  v-for="(pattern, i) in patterns"
                  :key="i"
                  class="mr-3 h-14 w-14 cursor-pointer border-2 rounded-lg"
                  :src="pattern"
                  alt="no-pattern"
                  :class="feature_form.fill_pattern === pattern ? 'border-blue-600' : ''"
                  @click="feature_form.fill_pattern = pattern"
                >
                <div
                  class="flex cursor-pointer items-center justify-center h-14 w-14 border-2 bg-gray-200 rounded-lg"
                  :class="!patterns.includes(feature_form.fill_pattern) ? 'border-blue-600' : ''"
                  @click="pattern_generate_popup.open()"
                >
                  <IconIcBaselineTune class="text-lg" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Footer -->
        <div class="flex items-center p-4 border-t border-t-gray-200" :class="[feature && !is_symbology ? 'justify-between' : 'justify-end']">
          <hawk-button
            v-if="feature && !is_symbology"
            color="error"
            type="link"
            @click="emit('delete')"
          >
            {{ $t('Delete') }}
          </hawk-button>

          <div class="flex">
            <hawk-button
              class="mr-5"
              type="outlined"
              @click="emit('close')"
            >
              {{ $t('Cancel') }}
            </hawk-button>
            <ButtonElement
              :button-label="$t('Save')"
              :submits="true"
            />
          </div>
        </div>
      </div>
    </Vueform>
  </hawk-modal-container>
</template>
